import { useState } from 'react'
import { Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { authSelector } from '../reducers'
import { AbsenceDays } from './cards/absencedays'
import { AbsenceHours } from './cards/absencehours'
import { Anniversaries } from './cards/anniversaries'
import { ChangedSalaryInformation } from './cards/changedsalaryinformation'
import { ChartGender } from './cards/chartgender'
import { EndingEmployments } from './cards/endingemployments'
import { EndingTrialPeriods } from './cards/endingtrialperiods'
import { EquipmentCount, EquipmentsOrdered, EquipmentsReturning } from './cards/equipments'
import { Expenses } from './cards/expenses'
import { ExperienceYearsChart } from './cards/experienceyears'
import { ExpiringEmployments } from './cards/expiringemployments'
import { NewContractsStarting } from './cards/newcontractsstarting'
import { OrderedBenefits } from './cards/orderedbenefits'
import { PayrollStatus } from './cards/payrollstatus'
import { PersonalInstructions } from './cards/personalinstructions'
import { PersonalRemainingPekkasHours } from './cards/personalremainingpekkashours'
import { PersonalRemainingVacationDays } from './cards/personalremainingvacationdays'
import { PersonalSupervisor } from './cards/personalsupervisor'
import { PersonalTasksAndGoals } from './cards/personaltasksandgoals'
import { PersonalTaxationInfo } from './cards/personaltaxationinfo'
import { PersonalVacationDays } from './cards/personalvacationdays'
import { PersonnelCount } from './cards/personnelcount'
import { PersonnelStats } from './cards/personnelstats'
import { PersonnelSummary } from './cards/personnelsummary'
import { Reminders } from './cards/reminders'
import { SicknessAbsenceDays } from './cards/sicknessabsencedays'
import { SicknessAbsenceHours } from './cards/sicknessabsencehours'
import { StartingEmployments } from './cards/startingemployments'
import { VacationDays } from './cards/vacationdays'
import { VacationDaysTable } from './cards/vacationdaystable'
import { dashboardKey, emptyCard, setDashboardKey } from './common'
import { DashboardCard } from './dashboardcard'

function restoreState(availableCards, { isAdmin, isHRUser, isPayroll, isSupervisor, isPersonal, isEquipment }) {
	const dashboardJson = localStorage.getItem(dashboardKey)

	if (dashboardJson !== null) {
		const restoredCards = JSON.parse(dashboardJson)
		const filteredCards = restoredCards.filter((card) => availableCards[card.name] !== undefined)
		return filteredCards.length === 0 ? [emptyCard()] : filteredCards
	}

	//ADMIN
	if (isAdmin | isHRUser && !isPersonal) {
		return [
			{ name: 'PersonnelSummary', size: 'lg' },
			{ name: 'PersonnelStatistics', size: 'lg' },
			{ name: 'Anniversaries', size: 'lg' },
			{ name: 'Reminders', size: 'lg' },
			{ name: 'GenderDistribution', size: 'lg' },
			{ name: 'PersonnelCount', size: 'lg' },
			{ name: 'EndingTrialPeriods', size: 'lg' },
			{ name: 'ExpiringFixedDurationEmployments', size: 'lg' },
			{ name: 'ExperienceYears', size: 'lg' },
			{ name: 'VacationDays', size: 'lg' },
		]
	}
	//OWNPROFILE
	if (isPersonal) {
		return [
			{ name: 'PersonalSupervisor', size: 'lg' },
			{ name: 'PersonalTasksAndGoals', size: 'lg' },
			{ name: 'PersonalInstructions', size: 'lg' },
		]
	}

	//CLERK
	if (isPayroll) {
		return [
			{ name: 'PersonnelSummary', size: 'lg' },
			{ name: 'StartingEmployments', size: 'lg' },
			{ name: 'NewContractsStarting', size: 'lg' },
			{ name: 'ChangedSalaryInformation', size: 'lg' },
			{ name: 'OrderedBenefits', size: 'lg' },
			{ name: 'ExpiringFixedDurationEmployments', size: 'lg' },
			{ name: 'EndingTrialPeriods', size: 'lg' },
			{ name: 'VacationDays', size: 'lg' },
			{ name: 'VacationDaysTable', size: 'lg' },
		]
	}

	//SUPERVISOR
	if (isSupervisor) {
		return [
			{ name: 'PersonnelSummary', size: 'lg' },
			{ name: 'PersonnelCount', size: 'lg' },
			{ name: 'Anniversaries', size: 'lg' },
			{ name: 'VacationDays', size: 'lg' },
			{ name: 'ExpiringFixedDurationEmployments', size: 'lg' },
			{ name: 'EndingTrialPeriods', size: 'lg' },
		]
	}

	//EQUIPMENT
	if (isEquipment) {
		return [
			{ name: 'EquipmentsOrdered', size: 'lg' },
			{ name: 'EquipmentsReturning', size: 'lg' },
			{ name: 'EquipmentsInUse', size: 'lg' },
		]
	}

	//DEFAULT
	return [
		{ name: 'PersonnelStatistics', size: 'lg' },
		{ name: 'PersonnelSummary', size: 'sm' },
	]
}

export function DashboardCards({ personalDashboardData }) {
	const { t } = useTranslation()

	const auth = useSelector(authSelector)
	const supervisor = auth.role === 'SUPERVISOR'
	const personal = location.pathname.startsWith('/personal')
	const payroll = auth.role === 'CLERK'
	const admin = auth.role === 'ADMIN'
	const hruser = auth.role === 'HRUSER'

	setDashboardKey(supervisor ? 'supervisor-dashboard' : personal ? 'personal-dashboard' : 'dashboard')
	const isBasicLevel = auth.hrLevel !== 'premium'

	// key is a translation key
	const availableCards = (() => {
		const cards = {
			Empty: () => (
				<span className="opacity-50">({t('SelectItemToShowData', 'Valitse kohde näyttääksesi tiedot')})</span>
			),
		}

		if (personal) {
			//OMA PROFIILI KORTIT
			cards.PersonalVacationDays = () => <PersonalVacationDays personalDashboardData={personalDashboardData} />
			cards.PersonalRemainingVacationDays = () => (
				<PersonalRemainingVacationDays personalDashboardData={personalDashboardData} />
			)
			cards.PersonalRemainingPekkasHours = () => (
				<PersonalRemainingPekkasHours personalDashboardData={personalDashboardData} />
			)
			cards.PersonalTaxationInfo = () => <PersonalTaxationInfo personalDashboardData={personalDashboardData} />
			cards.PersonalSupervisor = () => <PersonalSupervisor personalDashboardData={personalDashboardData} />
			cards.PersonalInstructions = () => <PersonalInstructions personalDashboardData={personalDashboardData} />
			cards.PersonalTasksAndGoals = () => <PersonalTasksAndGoals />
			return cards
		}

		// MOVED EQUIPMENT CARDS TO EQUIPMENT ROLE
		if (auth.role === 'EQUIPMENT' && !personal) {
			cards.EquipmentsOrdered = () => <EquipmentsOrdered />
			cards.EquipmentsReturning = () => <EquipmentsReturning />
			cards.EquipmentsInUse = () => <EquipmentCount />
			return cards
		}

		// JOS KÄYTTÄJÄ ON ADMIN TAI YLEINEN HR, ANNETAAN KAIKKI KORTIT
		if (admin | hruser) {
			cards.PersonnelSummary = () => <PersonnelSummary persons={personalDashboardData} />
			cards.PersonnelCount = (cardProps, changeProperty) => (
				<PersonnelCount cardProps={cardProps} changeProperty={changeProperty} />
			)
			cards.ExpiringFixedDurationEmployments = () => <ExpiringEmployments payrollView={true} />
			cards.EndingTrialPeriods = () => <EndingTrialPeriods />
			cards.Anniversaries = (cardProps, changeProperty) => (
				<Anniversaries cardProps={cardProps} changeProperty={changeProperty} />
			)
			cards.VacationDays = (cardProps, changeProperty) => (
				<VacationDays cardProps={cardProps} changeProperty={changeProperty} />
			)
			cards.SicknessAbsenceDays = () => <SicknessAbsenceDays />
			cards.SicknessAbsenceHours = () => <SicknessAbsenceHours />
			cards.PersonnelStatistics = () => <PersonnelStats persons={personalDashboardData} />
			cards.ExperienceYears = () => <ExperienceYearsChart persons={personalDashboardData} />
			cards.Expenses = () => <Expenses />
			cards.AbsenceDays = () => <AbsenceDays />
			cards.AbsenceHours = () => <AbsenceHours />
			cards.GenderDistribution = () => <ChartGender persons={personalDashboardData} />
			cards.PayrollStatus = () => <PayrollStatus />
			cards.Reminders = () => <Reminders />
			cards.OrderedBenefits = () => <OrderedBenefits />
			cards.StartingEmployments = () => <StartingEmployments />
			cards.NewContractsStarting = () => <NewContractsStarting />
			cards.ChangedSalaryInformation = () => <ChangedSalaryInformation />
			cards.EndingEmployments = () => <EndingEmployments title={t('EndingEmployments', 'Päättyvät työsuhteet')} />
			cards.VacationDaysTable = (cardProps, changeProperty) => (
				<VacationDaysTable cardProps={cardProps} changeProperty={changeProperty} />
			)
			cards.EquipmentsOrdered = () => <EquipmentsOrdered />
			cards.EquipmentsReturning = () => <EquipmentsReturning />
			cards.EquipmentsInUse = () => <EquipmentCount />
		} else if (payroll) {
			// PALKANLASKIJAN KORTIT
			cards.PersonnelSummary = () => <PersonnelSummary persons={personalDashboardData} />
			cards.VacationDays = (cardProps, changeProperty) => (
				<VacationDays cardProps={cardProps} changeProperty={changeProperty} />
			)
			cards.EndingTrialPeriods = () => <EndingTrialPeriods />
			cards.ExpiringFixedDurationEmployments = () => <ExpiringEmployments payrollView={true} />
			cards.OrderedBenefits = () => <OrderedBenefits />
			cards.StartingEmployments = () => <StartingEmployments />
			cards.VacationDaysTable = (cardProps, changeProperty) => (
				<VacationDaysTable cardProps={cardProps} changeProperty={changeProperty} />
			)
			cards.EndingEmployments = () => <EndingEmployments title={t('EndingEmployments', 'Päättyvät työsuhteet')} />
			cards.NewContractsStarting = () => <NewContractsStarting />
			cards.ChangedSalaryInformation = () => <ChangedSalaryInformation />
		} else if (supervisor) {
			//ESIHENKILÖN KORTIT
			cards.PersonnelSummary = () => <PersonnelSummary tokens={personalDashboardData} />
			cards.PersonnelCount = (cardProps, changeProperty) => (
				<PersonnelCount cardProps={cardProps} changeProperty={changeProperty} />
			)
			cards.ExpiringFixedDurationEmployments = () => <ExpiringEmployments />
			cards.EndingTrialPeriods = () => <EndingTrialPeriods />
			cards.Anniversaries = (cardProps, changeProperty) => (
				<Anniversaries cardProps={cardProps} changeProperty={changeProperty} />
			)
			cards.VacationDays = (cardProps, changeProperty) => (
				<VacationDays cardProps={cardProps} changeProperty={changeProperty} />
			)
			cards.SicknessAbsenceDays = () => <SicknessAbsenceDays />
			cards.SicknessAbsenceHours = () => <SicknessAbsenceHours />
		} else {
			//HR KORTIT
			cards.PersonnelStatistics = () => <PersonnelStats persons={personalDashboardData} />
			cards.PersonnelSummary = () => <PersonnelSummary persons={personalDashboardData} />
			cards.PersonnelCount = (cardProps, changeProperty) => (
				<PersonnelCount cardProps={cardProps} changeProperty={changeProperty} />
			)
			cards.ExpiringFixedDurationEmployments = () => <ExpiringEmployments />
			cards.EndingTrialPeriods = () => <EndingTrialPeriods />
			cards.ExperienceYears = () => <ExperienceYearsChart persons={personalDashboardData} />
			cards.Expenses = () => <Expenses />
			cards.Anniversaries = (cardProps, changeProperty) => (
				<Anniversaries cardProps={cardProps} changeProperty={changeProperty} />
			)
			cards.VacationDays = (cardProps, changeProperty) => (
				<VacationDays cardProps={cardProps} changeProperty={changeProperty} />
			)
			cards.AbsenceDays = () => <AbsenceDays />
			cards.AbsenceHours = () => <AbsenceHours />
			cards.SicknessAbsenceDays = () => <SicknessAbsenceDays />
			cards.SicknessAbsenceHours = () => <SicknessAbsenceHours />
			cards.GenderDistribution = () => <ChartGender persons={personalDashboardData} />
			cards.PayrollStatus = () => <PayrollStatus />
			cards.Reminders = () => <Reminders />
		}

		return cards
	})()

	const [cards, setCards] = useState(() =>
		restoreState(availableCards, {
			isAdmin: admin,
			isHRUser: hruser,
			isPayroll: payroll,
			isSupervisor: supervisor,
			isPersonal: personal,
			isEquipment: auth.role === 'EQUIPMENT',
		}),
	)

	const premiumCards = [
		//  "AbsenceHours",
		//  "AbsenceDays",
		//  "ExperienceYears",
	]

	const isPremiumCard = (name) => isBasicLevel && premiumCards.includes(name)
	const compareFn = (a, b) => t(a) > t(b) // sort options by translations
	const cardOptions = Object.keys(availableCards)
		.sort(compareFn)
		.map((name) => (
			<option key={name} className={isPremiumCard(name) ? 'premium' : ''} value={name}>
				{t(name)}
			</option>
		))

	return (
		<Row className="dashboard-cards">
			{cards.map((card, idx) => (
				<DashboardCard
					key={card.name}
					setCards={setCards}
					availableCards={availableCards}
					cardOptions={cardOptions}
					data={{
						...card,
						idx: idx,
					}}
				/>
			))}
		</Row>
	)
}
